"use client";

import { createContext, useContext, useRef, type ReactNode } from "react";

import { useInView } from "./motion";

const InViewContext = createContext(false);

/** Whether the enclosing <Section> is in view. Animated children start and
 *  stop on it, so a canvas never draws off-screen. */
export const useSectionInView = (): boolean => useContext(InViewContext);

/**
 * A landing section that knows when it is on screen. While it is, it carries
 * the `sq-in` class, which plays every `.sq-rv` reveal inside it (globals.css)
 * — and the class comes off again on exit, so the reveals replay next time,
 * as in the prototype. Children that need more than CSS read the same
 * signal through useSectionInView().
 */
export function Section({
  id,
  className = "",
  children,
}: {
  id?: string;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, 0.12);
  return (
    <InViewContext.Provider value={inView}>
      <section id={id} ref={ref} className={`relative ${inView ? "sq-in" : ""} ${className}`}>
        {children}
      </section>
    </InViewContext.Provider>
  );
}
